import {
	Breadcrumb,
	BreadcrumbItem,
	BreadcrumbList,
	BreadcrumbPage,
	BreadcrumbSeparator
} from '@/components/ui/breadcrumb';
import { ROUTES } from '@/constants';
import { BreadcrumbRouterLink } from '@/components/link/Link';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { DeployDeviceFormSchemaType, deployDeviceSchema, stepFields } from './deploy-device-schema';
import { Form } from '@/components/ui/form';
import { RHFAutoComplete } from '@/components/auto-complete/RHFAutoComplete';
import { useLoaderData, useNavigate, useRevalidator } from 'react-router';
import { DeployDeviceLoaderResponse } from './types';
import LandownerStep from './LandownerStep';
import FileUploadStep from './FileUploadStep';
import FieldReportStep from './FieldReportStep';
import { useMutation, useQuery } from '@tanstack/react-query';
import { deployDevice, queryProcessObjectsBySiteId, toDeployDeviceMetadata } from './constants';
import { Stepper } from './Stepper';
import { toast } from 'sonner';
import { getUIErrorMessage } from '@/utils/errors';
import useTitle from '@/hooks/useTitle.ts';

const steps = [
	{ name: 'Device details' },
	{ name: 'Landowner details' },
	{ name: 'Upload file' },
	{ name: 'Field report' }
];

export const DeployDevice = () => {
	useTitle("Deploy Device");

	const [currentStep, setCurrentStep] = useState(0);
	const [error, setError] = useState('');
	const revalidator = useRevalidator();

	const deployDeviceForm = useForm<DeployDeviceFormSchemaType>({
		mode: 'onChange',
		reValidateMode: 'onChange',
		resolver: zodResolver(deployDeviceSchema),
		defaultValues: {
			deploymentSiteId: [],
			deviceId: [],
			antennaTypeId: [],
			pvPanelTypeId: [],
			landownerExists: false,
			existingLandownerId: undefined,
			landowner: {
				firstName: '',
				lastName: '',
				streetAddress: '',
				city: '',
				state: '',
				zip: '',
				mailingAddress: '',
				email: '',
				phone: '',
				mobile: '',
				status: undefined,
				initialPayment: undefined,
				recurringPayment: undefined,
				latitude: undefined,
				longitude: undefined,
				initialDeploymentDate: undefined
			},
			upload: {
				processObjectId: '',
				title: '',
				description: '',
				file: undefined
			},
			fieldReport: {
				performedAt: undefined,
				deploymentTypeId: '',
				notes: '',
				hasExternalAntennas: false,
				swapReason: null
			}
		}
	});

	const { sites, devices, antennas, pvPanels, deploymentTypes, landowners } =
		useLoaderData() as DeployDeviceLoaderResponse;

	const siteId = deployDeviceForm.watch('deploymentSiteId')[0]?.value;

	const { data: processObjects } = useQuery({
		queryKey: ['process-objects', siteId],
		queryFn: () => queryProcessObjectsBySiteId(siteId),
		enabled: !!siteId
	});

	const availableProcessObjects = processObjects?.data || [];

	const navigate = useNavigate();

	const { isPending: isDeploying, mutate: mutateDeployDevice } = useMutation({
		mutationFn: deployDevice,
		mutationKey: ['devices'],
		onSuccess: data => {
			deployDeviceForm.reset();
			toast('Device deployed successfully');
			revalidator.revalidate();
			setError('');
			navigate(ROUTES.getDeviceURL(data?.data?.id.toString()));
		},
		onError: error => {
			setError(getUIErrorMessage(error));
		}
	});

	const handleDeployDevice = () => {
		const submitForm = deployDeviceForm.handleSubmit(values => {
			const metadata = toDeployDeviceMetadata(values, landowners.data);
			const jsonString = JSON.stringify(metadata);
			const metaDataFile = new File([jsonString], 'metadata.json', {
				type: 'application/json'
			});

			mutateDeployDevice({ file: values.upload.file, metadata: metaDataFile });
		});
		submitForm();
	};

	const handleStepChange = async (newStep: number) => {
		if (newStep > currentStep) {
			const valid = await deployDeviceForm.trigger(stepFields[currentStep]);
			if (!valid) {
				return;
			}
		}
		setCurrentStep(newStep);
	};

	return (
		<div className="flex flex-col gap-10">
			<Breadcrumb>
				<BreadcrumbList>
					<BreadcrumbItem>
						<BreadcrumbRouterLink to={ROUTES.home}>Home</BreadcrumbRouterLink>
					</BreadcrumbItem>
					<BreadcrumbSeparator />
					<BreadcrumbItem>
						<BreadcrumbPage>Deploy device</BreadcrumbPage>
					</BreadcrumbItem>
				</BreadcrumbList>
			</Breadcrumb>

			<div className="container mx-auto">
				<Stepper
					steps={steps}
					currentStep={currentStep}
					onStepChange={handleStepChange}
					isLoading={isDeploying}
					onSubmit={handleDeployDevice}
				/>
				<div className="flex mt-8 p-4 items-center">
					<Form {...deployDeviceForm}>
						<form className="flex w-full flex-col gap-3 items-center justify-center">
							{currentStep === 0 && (
								<div className="w-full flex flex-col gap-3 max-w-4xl">
									<RHFAutoComplete
										control={deployDeviceForm.control}
										closeOnSelect
										name="deploymentSiteId"
										label="Site"
										placeholder="Select site"
										items={sites.filter(s => s.id !== 0)}
										itemToOption={item => ({ value: item.id.toString(), label: item.name })}
									/>

									<RHFAutoComplete
										control={deployDeviceForm.control}
										closeOnSelect
										name="deviceId"
										label="Device"
										placeholder="Select a device"
										items={devices.filter(device => device.site == null)}
										itemToOption={item => ({
											value: item.id.toString(),
											label: item.id.toString()
										})}
									/>

									<RHFAutoComplete
										control={deployDeviceForm.control}
										closeOnSelect
										name="antennaTypeId"
										label="Antenna type"
										placeholder="Select the antenna type"
										items={antennas}
										itemToOption={item => ({ value: item.antennaId.toString(), label: item.model })}
									/>

									<RHFAutoComplete
										control={deployDeviceForm.control}
										closeOnSelect
										name="pvPanelTypeId"
										label="PV panel type"
										placeholder="Select the PV panel type"
										items={pvPanels}
										itemToOption={item => ({ value: item.panelId.toString(), label: item.model })}
									/>
								</div>
							)}

							{currentStep === 1 && <LandownerStep form={deployDeviceForm} />}

							{currentStep === 2 && (
								<FileUploadStep
									form={deployDeviceForm}
									processObjectIds={availableProcessObjects.map(po => po.id.toString())}
								/>
							)}

							{currentStep === 3 && (
								<FieldReportStep
									form={deployDeviceForm}
									availableProcessObjects={availableProcessObjects}
									deploymentTypes={deploymentTypes}
								/>
							)}
						</form>
					</Form>
				</div>

				{error && (
					<div className="text-red-500 text-sm text-center">
						<p>{error}</p>
					</div>
				)}
			</div>
		</div>
	);
};
